import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import DocumentEditor from "../editor/DocumentEditor";
import Header from "../layout/Header";
import "../../index.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

// Generate a day-month-year id like 23-10-2025
function formatJournalId(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
}

function isTokenValid(auth) {
  const expiresAt = auth.user?.expires_at;
  if (!auth.user || !auth.user.access_token || !expiresAt) return false;
  return Date.now() / 1000 < expiresAt;
}

// Remove token prop: always use auth context
function JournalEditor() {
  const auth = useAuth();
  const journalId = formatJournalId(new Date());

  // Fetch previous journal entries
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isTokenValid(auth)) {
          setEntries([]);
          return;
        }

        const url = `${API_BASE_URL}/documents?document_type=journal&include_content`;
        const res = await fetch(url, {
          signal: ctl.signal,
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        const contentType = res.headers.get("content-type") || "";
        const bodyText = await res.text();

        if (!res.ok) {
          throw new Error(
            `Failed to load journals (${res.status} ${res.statusText}): ${bodyText.slice(0, 200)}`
          );
        }
        if (!contentType.includes("application/json")) {
          throw new Error(
            `Expected JSON but got "${contentType || "unknown"}". Body: ${bodyText
              .replace(/\s+/g, " ")
              .slice(0, 200)}`
          );
        }

        const data = JSON.parse(bodyText);
        const items = Array.isArray(data) ? data : data.items || [];

        const normalized = items
          .filter((d) => {
            const t = d?.documentType || d?.document_type || d?.type;
            return t === "journal";
          })
          .filter((d) => (d.id || d._id || d.slug) !== journalId) // exclude today's
          .map((d) => ({
            id: d.id || d._id || d.slug,
            title: d.name || d.title || (d.id || d._id || d.slug),
            content: d.content || d.body || d.text || "",
            createdAt: d.createdAt ? new Date(d.createdAt) : (d.created_at ? new Date(d.created_at) : null),
            updatedAt: d.updatedAt ? new Date(d.updatedAt) : (d.updated_at ? new Date(d.updated_at) : null),
          }))
          .sort((a, b) => {
            const aTime = (a.updatedAt || a.createdAt || 0).valueOf();
            const bTime = (b.updatedAt || b.createdAt || 0).valueOf();
            return bTime - aTime;
          });

        setEntries(normalized);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();

    return () => ctl.abort();
  }, [journalId, auth.user]); // re-run when OIDC user becomes available

  const formatDisplayDate = (entry) => {
    const d = entry.updatedAt || entry.createdAt;
    if (!d) return "";
    return d.toLocaleString();
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center"
      style={{ backgroundColor: "#e6dcc6" }}
    >
      <div
        className="w-full max-w-5xl flex flex-col"
        style={{ backgroundColor: "#f7f2e3", minHeight: "100vh", padding: 0 }}
      >
        <Header
          user={auth.user}
          logoSrc="/mast_branding_without_name.png"
          centerControl={
            <h1 className="w-full text-center text-2xl font-semibold text-gray-900">
              Journal Entry
            </h1>
          }
          onLogout={() => {
            if (auth?.signoutRedirect) auth.signoutRedirect();
            else if (auth?.removeUser) auth.removeUser();
          }}
        />
        <div className="flex-1 flex flex-col w-full h-full">
          <div
            className="w-full bg-white p-1"
            style={{ height: "50vh", overflow: "auto" }}
          >
            <DocumentEditor
              id={journalId}
              showHeader={false}
              enableRevisions={false}
              documentType="journal"
              defaultName={`Journal - ${journalId}`}
            />
          </div>
          <div
            className="w-full p-4 border-t"
            style={{
              height: "50vh",
              overflow: "auto",
              backgroundColor: "#f3efe2",      // softer panel background
              borderTop: "1px solid #e4dccb",  // subtle divider
            }}
          >
            {loading && <div>Loading...</div>}
            {error && <div style={{ color: "red" }}>{error}</div>}
            {!loading && !error && entries.length === 0 && (
              <div>No previous entries found.</div>
            )}
            {!loading &&
              !error &&
              entries.map((e) => (
                <article
                  key={e.id}
                  style={{
                    background: "#fffdf7",                       // off‑white card
                    borderRadius: 8,
                    padding: 14,
                    marginBottom: 12,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",     // soft shadow
                    border: "1px solid rgba(0,0,0,0.04)",        // very light border
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b5e4a",                           // warmer date color
                      marginBottom: 8,
                      fontWeight: 500,
                      letterSpacing: 0.2,
                    }}
                  >
                    {(e.updatedAt || e.createdAt)?.toLocaleString?.() || ""}
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "inherit",
                      color: "#2a2a2a",
                    }}
                  >
                    {e.content}
                  </pre>
                </article>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default JournalEditor;
